import { useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    EventNote as ScheduleIcon,
    Person as PersonIcon,
} from '@mui/icons-material';

import {
    useCreateScheduleTemplateMutation,
    useDeleteScheduleTemplateMutation,
    useGetCategoriesQuery,
    useGetScheduleTemplateSetsQuery,
    useGetScheduleTemplatesQuery,
    useGetUsersQuery,
    useUpdateScheduleTemplateMutation,
} from '@/store/apiSlice';
import type { ScheduleTemplate } from '@/types';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppDialog } from '@/components/dialogs/AppDialog';
import { getApiErrorMessage } from '@/utils/apiError';
import { createCategoryPlaceholder } from '@/utils/schedulePlaceholders';
import CategoryIcon from '@/components/CategoryIcon';
import { splitTags } from '@/utils/tags';

type ResponsibleOption =
    | { type: 'person'; name: string }
    | { type: 'category'; name: string; icon: string };

type Props = {
    open: boolean;
    onClose: () => void;
    canEdit: boolean;
};

const ScheduleTemplatesDialog = ({ open, onClose, canEdit }: Props) => {
    const { LL } = useI18nContext();
    const { alert, confirm } = useAppDialog();

    const {
        data: templates = [],
        isFetching: isFetchingTemplates,
        refetch: refetchTemplates,
    } = useGetScheduleTemplatesQuery(undefined, { skip: !open });

    // only used for showing "used by" counts
    const { data: templateSets } = useGetScheduleTemplateSetsQuery(undefined, { skip: !open });

    const { data: users } = useGetUsersQuery(undefined, { skip: !open });
    const { data: categories } = useGetCategoriesQuery(undefined, { skip: !open });

    const [createTemplate] = useCreateScheduleTemplateMutation();
    const [updateTemplate] = useUpdateScheduleTemplateMutation();
    const [deleteTemplate] = useDeleteScheduleTemplateMutation();

    const [editingTemplate, setEditingTemplate] = useState<ScheduleTemplate | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [formData, setFormData] = useState({
        template: '',
        duration: 5,
        responsible: '',
        tags: '',
    });

    const responsibleOptions: ResponsibleOption[] = [
        ...(categories?.map((cat) => ({
            type: 'category' as const,
            name: cat.name,
            icon: cat.icon,
        })) || []),
        ...(users?.map((user) => ({
            type: 'person' as const,
            name: user.name,
        })) || []),
    ];

    const usedBySetsCountByTemplateId = (() => {
        const counts = new Map<number, number>();
        for (const set of templateSets ?? []) {
            for (const t of set.templates ?? []) {
                counts.set(t.id, (counts.get(t.id) ?? 0) + 1);
            }
        }
        return counts;
    })();

    const handleOpenEdit = (template?: ScheduleTemplate) => {
        if (!canEdit) return;
        if (template) {
            setEditingTemplate(template);
            setFormData({
                template: template.template,
                duration: template.duration,
                responsible: template.responsible || '',
                tags: template.tags || '',
            });
        } else {
            setEditingTemplate(null);
            setFormData({
                template: '',
                duration: 5,
                responsible: '',
                tags: '',
            });
        }
        setEditDialogOpen(true);
    };

    const handleCloseEdit = () => {
        setEditDialogOpen(false);
        setEditingTemplate(null);
    };

    const handleSave = async () => {
        if (!canEdit) return;
        if (!formData.template.trim()) {
            await alert(LL.adminScheduleTemplatesPage.templateNameRequired());
            return;
        }

        try {
            if (editingTemplate) {
                await updateTemplate({ id: editingTemplate.id, ...formData }).unwrap();
            } else {
                await createTemplate(formData).unwrap();
            }
            handleCloseEdit();
        } catch (error) {
            console.error('Failed to save template:', error);
            await alert(
                LL.adminScheduleTemplatesPage.saveFailed({ message: getApiErrorMessage(error) })
            );
        }
    };

    const handleDelete = async (id: number) => {
        if (!canEdit) return;
        if (!(await confirm(LL.adminScheduleTemplatesPage.deleteConfirm()))) return;
        try {
            await deleteTemplate(id).unwrap();
        } catch (error) {
            console.error('Failed to delete template:', error);
            await alert(
                LL.adminScheduleTemplatesPage.deleteFailed({ message: getApiErrorMessage(error) })
            );
        }
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1.5, sm: 2 },
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: { xs: 1, sm: 2 },
                        }}
                    >
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <ScheduleIcon fontSize="small" />
                            <Typography variant="h6">
                                {LL.adminScheduleTemplatesPage.title()}
                            </Typography>
                        </Stack>
                        <Button
                            onClick={() => refetchTemplates()}
                            disabled={isFetchingTemplates}
                            size="small"
                        >
                            {LL.common.refresh()}
                        </Button>
                    </Stack>
                </DialogTitle>
                <DialogContent
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1.5, sm: 2 },
                    }}
                >
                    {!canEdit && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                mb: { xs: 1, sm: 2 },
                            }}
                        >
                            {LL.errors.unauthorized()}
                        </Typography>
                    )}

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        sx={{
                            gap: { xs: 1, sm: 2 },
                            mb: { xs: 1, sm: 2 },
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenEdit()}
                            disabled={!canEdit}
                            size="small"
                        >
                            {LL.adminScheduleTemplatesPage.addTemplate()}
                        </Button>
                    </Stack>

                    <Stack
                        sx={{
                            gap: { xs: 0.75, sm: 1 },
                        }}
                    >
                        {templates.map((t) => {
                            const tags = splitTags(t.tags);
                            const titleWithTags = tags.length
                                ? `${tags.join(', ')} - ${t.template}`
                                : t.template;

                            return (
                                <Box
                                    key={t.id}
                                    sx={{
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        px: { xs: 1, sm: 1.5 },
                                        py: { xs: 0.75, sm: 1 },
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: { xs: 1, sm: 1.5 },
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack
                                            direction="row"
                                            sx={{
                                                gap: { xs: 0.5, sm: 1 },
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Typography
                                                noWrap
                                                sx={{
                                                    fontWeight: 700,
                                                    maxWidth: 420,
                                                }}
                                            >
                                                {titleWithTags}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={`${t.duration} ${LL.scheduleModal.minutesShort()}`}
                                            />
                                            {t.responsible && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={t.responsible}
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                    <Tooltip title={LL.adminScheduleTemplatesPage.usedBySets()}>
                                        <Chip
                                            label={usedBySetsCountByTemplateId.get(t.id) ?? 0}
                                            size="small"
                                            color={
                                                usedBySetsCountByTemplateId.get(t.id)
                                                    ? 'warning'
                                                    : 'default'
                                            }
                                            variant="filled"
                                        />
                                    </Tooltip>
                                    <Tooltip title={LL.adminScheduleTemplatesPage.editTemplate()}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenEdit(t)}
                                                disabled={!canEdit}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={LL.common.delete()}>
                                        <span>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(t.id)}
                                                disabled={!canEdit}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            );
                        })}

                        {templates.length === 0 && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                }}
                            >
                                {LL.adminScheduleTemplatesPage.noTemplates()}
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.5 },
                    }}
                >
                    <Button onClick={onClose} size="small">
                        {LL.common.close()}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Sub-dialog for create/edit */}
            <Dialog open={editDialogOpen} onClose={handleCloseEdit} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        px: { xs: 1.5, sm: 3 },
                        py: { xs: 1.25, sm: 2 },
                    }}
                >
                    {editingTemplate
                        ? LL.adminScheduleTemplatesPage.editTemplate()
                        : LL.adminScheduleTemplatesPage.addTemplate()}
                </DialogTitle>
                <DialogContent
                    sx={{
                        px: { xs: 1.5, sm: 3 },
                        py: { xs: 1.25, sm: 2 },
                    }}
                >
                    <Stack spacing={{ xs: 1.5, sm: 3 }} sx={{ mt: { xs: 0.5, sm: 2 } }}>
                        <TextField
                            label={LL.adminScheduleTemplatesPage.templateName()}
                            value={formData.template}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, template: e.target.value }))
                            }
                            fullWidth
                            required
                            autoFocus
                        />

                        <TextField
                            label={LL.adminScheduleTemplatesPage.templateDuration()}
                            type="number"
                            value={formData.duration}
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    duration: parseInt(e.target.value, 10) || 0,
                                }))
                            }
                            fullWidth
                            required
                        />

                        <Autocomplete
                            freeSolo
                            options={responsibleOptions}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option : option.name
                            }
                            value={formData.responsible}
                            groupBy={(option) =>
                                typeof option === 'string'
                                    ? ''
                                    : option.type === 'person'
                                      ? LL.scheduleModal.person()
                                      : LL.scheduleModal.category()
                            }
                            onChange={(_, newValue) => {
                                if (typeof newValue === 'object' && newValue !== null) {
                                    const valueToAdd =
                                        newValue.type === 'category'
                                            ? createCategoryPlaceholder(newValue.name)
                                            : newValue.name;
                                    setFormData((prev) => ({
                                        ...prev,
                                        responsible: valueToAdd,
                                    }));
                                } else {
                                    setFormData((prev) => ({
                                        ...prev,
                                        responsible: newValue || '',
                                    }));
                                }
                            }}
                            onInputChange={(_, newInputValue) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    responsible: newInputValue,
                                }));
                            }}
                            renderOption={(props, option) => {
                                if (typeof option === 'string') {
                                    return (
                                        <ListItem {...props}>
                                            <ListItemText primary={option} />
                                        </ListItem>
                                    );
                                }
                                return (
                                    <ListItem {...props}>
                                        <ListItemIcon>
                                            {option.type === 'category' ? (
                                                <CategoryIcon category={option.name} />
                                            ) : (
                                                <PersonIcon />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText primary={option.name} />
                                    </ListItem>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={LL.scheduleModal.person()}
                                    placeholder={LL.scheduleModal.person()}
                                    fullWidth
                                    required
                                    slotProps={{
                                        ...params.slotProps,

                                        input: {
                                            ...params.slotProps.input,
                                            autoComplete: 'off',
                                        },
                                    }}
                                />
                            )}
                            sx={{
                                '& .MuiAutocomplete-input': {
                                    p: { xs: 1, sm: 1.5 },
                                },
                            }}
                        />

                        <TextField
                            label={LL.scheduleModal.tagsLabel()}
                            value={formData.tags}
                            onChange={(e) =>
                                setFormData((prev) => ({ ...prev, tags: e.target.value }))
                            }
                            fullWidth
                            placeholder={LL.scheduleModal.tagsPlaceholder()}
                            helperText={LL.scheduleModal.tagsHelperText()}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 1.5, sm: 3 },
                        py: { xs: 0.75, sm: 1.5 },
                    }}
                >
                    <Button onClick={handleCloseEdit} size="small">
                        {LL.common.cancel()}
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        size="small"
                        disabled={!canEdit}
                    >
                        {LL.common.save()}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ScheduleTemplatesDialog;
